/* towsons-giveback-share — makes a card out of the photo they just picked.
 *
 * Asking somebody to "post about it and send us the link" is three chores.
 * This turns it into one: the moment they choose a photo, a finished square
 * card appears — their picture in our frame, with the hashtag and the shop's
 * name on it — and a button that opens their phone's own share sheet with the
 * image already attached.
 *
 * ALL OF IT HAPPENS IN THE BROWSER. The card is drawn on a canvas from the
 * File they picked; nothing is uploaded to make it, and no copy of it reaches
 * us unless they also send the claim. On a page that is already asking for a
 * photograph taken in public, that is the right default.
 *
 * WHAT IT DOES NOT DO is prove anything. Tapping share tells us nothing — we
 * cannot read anybody's feed. The 75% tier still rests on a link they paste,
 * which is why the link field sits directly under these buttons and not
 * somewhere else on the page.
 *
 * Instagram has no way to be handed a post from a web page, on purpose. On a
 * phone the native share sheet solves it; on a desktop the honest answer is
 * "download this and post it from your phone", so that is what it says.
 */
(function () {
  'use strict';

  var SIZE = 1080;              // square, which every network accepts
  var PAD = 64;
  var JPEG_QUALITY = 0.92;

  function rounded(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  /* Their photo, cropped to fill the frame rather than squashed into it. */
  function drawCover(ctx, img, x, y, w, h) {
    var ir = img.width / img.height;
    var fr = w / h;
    var sw = img.width, sh = img.height, sx = 0, sy = 0;
    if (ir > fr) { sw = img.height * fr; sx = (img.width - sw) / 2; }
    else { sh = img.width / fr; sy = (img.height - sh) / 2; }
    ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
  }

  function mugMark(ctx, cx, cy, scale, colour) {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.scale(scale, scale);
    ctx.strokeStyle = colour;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 6;
    ctx.beginPath();                       // the arc coming back over
    ctx.moveTo(-45, -25);
    ctx.quadraticCurveTo(0, -65, 45, -25);
    ctx.stroke();
    ctx.beginPath();                       // its head
    ctx.moveTo(45, -25); ctx.lineTo(33, -37); ctx.lineTo(52, -40);
    ctx.closePath();
    ctx.fillStyle = colour;
    ctx.fill();
    ctx.lineWidth = 7;
    rounded(ctx, -32, -5, 64, 58, 9);      // the mug
    ctx.stroke();
    ctx.beginPath();                       // its handle
    ctx.moveTo(32, 8);
    ctx.bezierCurveTo(57, 8, 57, 40, 32, 40);
    ctx.stroke();
    ctx.restore();
  }

  /**
   * Build the card. Returns a Blob, or null if the picture could not be read —
   * which is not worth an error message, because the claim itself does not
   * depend on this.
   */
  async function compose(file, opts) {
    var cream = opts.cream || '#EDE7DA';
    var ink = opts.ink || '#26201A';
    var sage = opts.sage || '#4B6E58';
    var display = opts.displayFont || 'Georgia, serif';

    var img;
    try {
      // from-image applies the EXIF rotation, so a photo taken in portrait is
      // not sideways on the card.
      img = await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch (e) {
      try { img = await createImageBitmap(file); } catch (e2) { return null; }
    }

    var c = document.createElement('canvas');
    c.width = SIZE; c.height = SIZE;
    var ctx = c.getContext('2d');
    if (!ctx) return null;

    ctx.fillStyle = cream;
    ctx.fillRect(0, 0, SIZE, SIZE);

    var photoH = 620;
    ctx.save();
    rounded(ctx, PAD, PAD, SIZE - PAD * 2, photoH, 28);
    ctx.clip();
    drawCover(ctx, img, PAD, PAD, SIZE - PAD * 2, photoH);
    ctx.restore();
    if (img.close) img.close();

    var textTop = PAD + photoH + 58;

    mugMark(ctx, PAD + 46, textTop + 6, 0.62, sage);

    ctx.fillStyle = ink;
    ctx.textBaseline = 'alphabetic';
    ctx.font = '600 56px ' + display;
    ctx.fillText(opts.line1 || 'I gave my mug', PAD + 108, textTop + 6);
    ctx.fillText(opts.line2 || 'a second life.', PAD + 108, textTop + 68);

    ctx.fillStyle = sage;
    ctx.font = '600 30px ' + (opts.uiFont || 'system-ui, sans-serif');
    ctx.fillText(opts.hashtag || '#MugDonation', PAD + 108, textTop + 126);

    ctx.fillStyle = 'rgba(38,32,25,0.55)';
    ctx.font = '500 26px ' + (opts.uiFont || 'system-ui, sans-serif');
    var domain = opts.domain || location.hostname.replace(/^www\./, '');
    ctx.fillText(domain, SIZE - PAD - ctx.measureText(domain).width, textTop + 126);

    return await new Promise(function (resolve) {
      c.toBlob(function (b) { resolve(b); }, 'image/jpeg', JPEG_QUALITY);
    });
  }

  function init(root) {
    var panel = root.querySelector('[data-tgbk-share]');
    var photo = root.querySelector('[data-tgbk-form] [name="photo"]');
    if (!panel || !photo) return;

    var preview = panel.querySelector('[data-tgbk-share-preview]');
    var shareBtn = panel.querySelector('[data-tgbk-share-native]');
    var dlBtn = panel.querySelector('[data-tgbk-share-download]');
    var note = panel.querySelector('[data-tgbk-share-note]');
    var links = panel.querySelectorAll('[data-tgbk-share-to]');

    var blob = null;
    var url = null;
    var busy = false;

    var text = panel.dataset.shareText || 'I gave my mug a second life. #MugDonation';
    var pageUrl = panel.dataset.shareUrl || location.origin + location.pathname;

    function styleOf(name, fallback) {
      var v = getComputedStyle(root).getPropertyValue(name);
      return (v && v.trim()) || fallback;
    }

    async function rebuild() {
      var f = photo.files && photo.files[0];
      if (!f) { panel.hidden = true; return; }
      if (busy) return;
      busy = true;

      // Wait for the display face, or the card is drawn in a fallback serif
      // that does not match the page it came from.
      try { if (document.fonts && document.fonts.ready) await document.fonts.ready; } catch (e) { /* draw anyway */ }

      blob = await compose(f, {
        cream: styleOf('--tgbk-cream', '#EDE7DA'),
        ink: styleOf('--tgbk-ink', '#26201A'),
        sage: styleOf('--tgbk-sage', '#4B6E58'),
        // The card is drawn in the page's OWN font — the theme's — so the
        // image matches the page it came from. Read off the computed style
        // rather than named, because the theme picks it, not us.
        displayFont: (getComputedStyle(root).fontFamily || 'sans-serif'),
        line1: panel.dataset.line1,
        line2: panel.dataset.line2,
        hashtag: panel.dataset.hashtag,
        domain: panel.dataset.domain,
      });
      busy = false;

      if (!blob) { panel.hidden = true; return; }
      if (url) URL.revokeObjectURL(url);
      url = URL.createObjectURL(blob);
      preview.src = url;
      dlBtn.href = url;
      panel.hidden = false;
    }

    function cardFile() {
      return new File([blob], 'mug-give-back.jpg', { type: 'image/jpeg' });
    }

    // Only offer the native sheet when this browser will actually take a file.
    // A share button that drops the picture is worse than no share button.
    function canShareFile() {
      if (!navigator.share || !navigator.canShare || !blob) return false;
      try { return navigator.canShare({ files: [cardFile()] }); }
      catch (e) { return false; }
    }

    function refreshButtons() {
      var native = canShareFile();
      shareBtn.hidden = !native;
      if (note) {
        note.textContent = native
          ? 'Share it, then paste the link below.'
          : 'Save the card, post it, then paste the link below. Instagram only takes posts from the app.';
      }
    }

    photo.addEventListener('change', function () {
      rebuild().then(refreshButtons);
    });

    shareBtn.addEventListener('click', function () {
      if (!canShareFile()) return;
      navigator.share({ files: [cardFile()], text: text }).catch(function () {
        /* They closed the sheet. Nothing to report — it is not a failure. */
      });
    });

    for (var i = 0; i < links.length; i++) {
      (function (a) {
        a.addEventListener('click', function (ev) {
          // The card cannot be attached programmatically on any of these, so
          // it is saved first and the composer opens with the words ready.
          if (url) {
            var tmp = document.createElement('a');
            tmp.href = url;
            tmp.download = 'mug-give-back.jpg';
            document.body.appendChild(tmp);
            tmp.click();
            tmp.remove();
          }
          var to = a.dataset.tgbkShareTo;
          var t = encodeURIComponent(text + ' ' + pageUrl);
          var u = encodeURIComponent(pageUrl);
          var target =
            to === 'x' ? 'https://twitter.com/intent/tweet?text=' + t :
            to === 'facebook' ? 'https://www.facebook.com/sharer/sharer.php?u=' + u :
            to === 'threads' ? 'https://www.threads.net/intent/post?text=' + t :
            to === 'bluesky' ? 'https://bsky.app/intent/compose?text=' + t : '';
          if (!target) { ev.preventDefault(); return; }
          a.href = target;
        });
      })(links[i]);
    }
  }

  function boot() {
    var roots = document.querySelectorAll('[data-towsons-giveback]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
